import type { DefineDeviceDefinitionInput } from "@repo/api/schemas";
import type { DeviceDefinitionDto as DeviceDefinition } from "@repo/data-store";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import {
  clinicalDefinitionParameterFormSchema,
  clinicalValueToForm,
  clinicalValueToInput,
  type DefinitionMeta,
} from "./clinical-definition-form";

/**
 * A kind's whole revisable content is its parameters. `manufacturer` and
 * `modelNumber` are here because the CREATE form collects them, and they are
 * dropped on the way into a revision — the domain sets them once and carries
 * them forward, so a revision that sent them would have them ignored.
 */
export const deviceDefinitionFormSchema = z.object({
  manufacturer: z.string(),
  modelNumber: z.string(),
  parameters: z.array(clinicalDefinitionParameterFormSchema),
});

export type DeviceDefinitionFormValues = z.infer<typeof deviceDefinitionFormSchema>;

export const EMPTY_DEVICE_DEFINITION: DeviceDefinitionFormValues = {
  manufacturer: "",
  modelNumber: "",
  parameters: [],
};

export function deviceDefinitionToDefaults(definition?: DeviceDefinition | null): DeviceDefinitionFormValues {
  if (!definition) return structuredClone(EMPTY_DEVICE_DEFINITION);
  return {
    manufacturer: definition.manufacturer ?? "",
    modelNumber: definition.modelNumber ?? "",
    parameters: definition.parameters.map((parameter) => ({
      id: parameter.id,
      label: parameter.label,
      value: clinicalValueToForm(parameter.value),
    })),
  };
}

export function deviceDefinitionValuesToInput(
  meta: DefinitionMeta,
  values: DeviceDefinitionFormValues,
): Omit<DefineDeviceDefinitionInput, "definitionUrl"> {
  return {
    name: meta.name,
    ...(meta.description.trim() ? { description: meta.description } : {}),
    ...(values.manufacturer.trim() ? { manufacturer: values.manufacturer } : {}),
    ...(values.modelNumber.trim() ? { modelNumber: values.modelNumber } : {}),
    parameters: values.parameters.map((parameter) => ({
      ...(parameter.id ? { id: parameter.id } : {}),
      label: parameter.label,
      value: clinicalValueToInput(parameter.value),
    })),
  };
}

export function useDeviceDefinitionForm(definition: DeviceDefinition | null) {
  return useAppForm({
    defaultValues: deviceDefinitionToDefaults(definition),
    validators: { onDynamic: deviceDefinitionFormSchema },
    onSubmit: () => {},
    onSubmitInvalid: focusFirstInvalid,
  });
}

export type DeviceDefinitionForm = ReturnType<typeof useDeviceDefinitionForm>;
